// src/Product/Accounting/BankTransactions.js
import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import Papa from "papaparse";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { v4 as uuidv4 } from "uuid";
import "./BankTransactions.css";

const TABS = ["Uncategorized", "Categorized", "Deleted"];
const PAGE_SIZE = 100;

// Mirrors are no longer needed when we post proper journal entries.
// We still keep the prefix constant to remain backward-compatible with any existing mirror data.
const MIRROR_PREFIX = "Mirror — ";
const MIRROR_PATTERN = `${MIRROR_PREFIX}%`;

// Bank Transaction type meaning:
// - Credit: money came IN to bank
// - Debit: money went OUT from bank
function buildJournalRowsForBankTx({
  tx,
  companyId,
  bankAccountId,
  categoryAccountId,
  description,
}) {
  const amount = Number(tx.amount || 0);

  // Credit: Debit Bank, Credit Category acct
  // Debit:  Credit Bank, Debit Category acct
  const isCredit = String(tx.type).toLowerCase() === "credit";

  const bankRow = {
    account_id: bankAccountId,
    date: tx.date,
    description,
    company_id: companyId,
    money_in: isCredit ? amount : 0,
    money_out: isCredit ? 0 : amount,
  };

  const categoryRow = {
    account_id: categoryAccountId,
    date: tx.date,
    description,
    company_id: companyId,
    money_in: isCredit ? 0 : amount,
    money_out: isCredit ? amount : 0,
  };

  return [bankRow, categoryRow];
}

function AccountSearchSelect({
  accounts = [],
  value,
  onChange,
  placeholder = "Search and select account",
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const inputRef = useRef(null);

  const selectedAccount = accounts.find((a) => a.id === value);

  const updateMenuPosition = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;

    setMenuStyle({
      top: rect.bottom - 370,
      left: rect.left,
      width: Math.max(rect.width, 420),
    });
  };

  const filteredGroupedAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filtered = accounts.filter((a) => {
      const name = String(a.name || "").toLowerCase();
      const type = String(a.type || "").toLowerCase();
      const category = String(a.category || "").toLowerCase();

      return !q || name.includes(q) || type.includes(q) || category.includes(q);
    });

    return filtered.reduce((groups, account) => {
      const groupName = account.type || "Other";
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(account);
      return groups;
    }, {});
  }, [accounts, search]);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open]);

  return (
    <div className="account-search-select">
      <input
        ref={inputRef}
        type="text"
        value={open ? search : selectedAccount?.name || ""}
        placeholder={selectedAccount?.name || placeholder}
        onFocus={() => {
          setOpen(true);
          setSearch("");
          setTimeout(updateMenuPosition, 0);
        }}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
          setTimeout(updateMenuPosition, 0);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 150);
        }}
      />

      {open && (
        <div className="account-search-menu" style={menuStyle}>
          {Object.keys(filteredGroupedAccounts).length ? (
            Object.entries(filteredGroupedAccounts).map(([type, group]) => (
              <div key={type} className="account-search-group">
                <div className="account-search-group-title">{type}</div>

                {group.map((account) => (
                  <button
                    type="button"
                    key={account.id}
                    className={`account-search-option ${
                      value === account.id ? "selected" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(account.id);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <span>{account.name}</span>
                    <small>{account.category || "Account"}</small>
                  </button>
                ))}
              </div>
            ))
          ) : (
            <div className="account-search-empty">
              No matching account found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BankTransactions() {
  const { companyId } = useCompany();

  // Core state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState("Uncategorized");

  // Counts for each tab (server-accurate)
  const [tabCounts, setTabCounts] = useState({
    Uncategorized: 0,
    Categorized: 0,
    Deleted: 0,
  });

  // Filters
  const [filterText, setFilterText] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterMinAmt, setFilterMinAmt] = useState("");
  const [filterMaxAmt, setFilterMaxAmt] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Accounts for categorization
  const [accounts, setAccounts] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [accountSelections, setAccountSelections] = useState({});
  const [multiAccountId, setMultiAccountId] = useState("");
  const [isCategorizing, setIsCategorizing] = useState(false);

  // CSV upload
  const [csvFile, setCsvFile] = useState(null);

  // Derived
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil((tabCounts[activeTab] || 0) / PAGE_SIZE)),
    [tabCounts, activeTab]
  );

  // Debounce filters to reduce round-trips
  const debounceTimer = useRef(null);
  const [debouncedFilters, setDebouncedFilters] = useState({
    filterText: "",
    filterType: "",
    filterDateFrom: "",
    filterDateTo: "",
    filterMinAmt: "",
    filterMaxAmt: "",
  });

  const commitFiltersWithDebounce = useCallback(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters({
        filterText,
        filterType,
        filterDateFrom,
        filterDateTo,
        filterMinAmt,
        filterMaxAmt,
      });
      setCurrentPage(1); // reset to first page on new filter set
    }, 300);
  }, [
    filterText,
    filterType,
    filterDateFrom,
    filterDateTo,
    filterMinAmt,
    filterMaxAmt,
  ]);

  useEffect(() => {
    commitFiltersWithDebounce();
    return () => clearTimeout(debounceTimer.current);
  }, [commitFiltersWithDebounce]);

  /** Fetch helpers */
  const fetchBankAccounts = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("chart_of_accounts")
      .select("id, name, category")
      .eq("company_id", companyId)
      .eq("category", "Bank");

    if (!error) setBankAccounts(data || []);
  }, [companyId]);

  const fetchAccounts = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("chart_of_accounts")
      .select("id, name, category, type")
      .eq("company_id", companyId)
      .order("type", { ascending: true })
      .order("name", { ascending: true });

    if (!error) setAccounts(data || []);
  }, [companyId]);

  // Tab counts exclude any legacy mirror rows (if they exist)
  const fetchTabCounts = useCallback(async () => {
    if (!companyId || !selectedAccount) return;

    const next = {};

    await Promise.all(
      TABS.map(async (status) => {
        let q = supabase
          .from("bank_transactions")
          .select("id", { head: true, count: "exact" })
          .eq("company_id", companyId)
          .eq("bank_account_id", selectedAccount)
          .eq("status", status);

        // hide mirrors (legacy)
        q = q.not("description", "ilike", MIRROR_PATTERN);

        const { count } = await q;
        next[status] = count || 0;
      })
    );

    setTabCounts(next);
  }, [companyId, selectedAccount]);

  // Fetch transactions excludes mirror rows (legacy)
  const fetchTransactions = useCallback(async () => {
    if (!companyId || !selectedAccount) {
      setTransactions([]);
      return;
    }

    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE - 1;

    let query = supabase
      .from("bank_transactions")
      .select("*", { count: "exact" })
      .eq("company_id", companyId)
      .eq("bank_account_id", selectedAccount)
      .eq("status", activeTab)
      .not("description", "ilike", MIRROR_PATTERN);

    const {
      filterText: s,
      filterType: t,
      filterDateFrom: df,
      filterDateTo: dt,
      filterMinAmt: minA,
      filterMaxAmt: maxA,
    } = debouncedFilters;

    if (s) query = query.ilike("description", `%${s}%`);
    if (t) query = query.eq("type", t);
    if (df) query = query.gte("date", df);
    if (dt) query = query.lte("date", dt);
    if (minA) query = query.gte("amount", +minA);
    if (maxA) query = query.lte("amount", +maxA);

    const { data, error } = await query
      .order("date", { ascending: true })
      .range(start, end);

    if (!error) {
      setTransactions(data || []);
      setSelectedTransactions([]);
      setAccountSelections({});
      setMultiAccountId("");
    }
  }, [companyId, selectedAccount, activeTab, currentPage, debouncedFilters]);

  /** Initial loads */
  useEffect(() => {
    fetchBankAccounts();
    fetchAccounts();
  }, [fetchBankAccounts, fetchAccounts]);

  /** React to account / tab / page / filters */
  useEffect(() => {
    fetchTransactions();
    fetchTabCounts();
  }, [fetchTransactions, fetchTabCounts]);

  /** Selection helpers */
  const idsOnPage = useMemo(
    () => transactions.map((t) => t.id),
    [transactions]
  );

  const allSelectedOnPage = useMemo(
    () =>
      idsOnPage.length > 0 &&
      idsOnPage.every((id) => selectedTransactions.includes(id)),
    [idsOnPage, selectedTransactions]
  );

  const toggleSelectAllOnPage = () => {
    if (allSelectedOnPage) {
      setSelectedTransactions((prev) =>
        prev.filter((id) => !idsOnPage.includes(id))
      );
    } else {
      setSelectedTransactions((prev) =>
        Array.from(new Set([...prev, ...idsOnPage]))
      );
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedTransactions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setPerRowAccount = (txId, accountId) => {
    setAccountSelections((prev) => ({ ...prev, [txId]: accountId }));
    if (!selectedTransactions.includes(txId)) {
      setSelectedTransactions((prev) => [...prev, txId]);
    }
  };

  /** CSV upload */
  const handleUploadCSV = () => {
    if (!csvFile) return alert("Please select a CSV file.");
    if (!selectedAccount) return alert("Select a bank account first.");

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data: rows, errors }) => {
        if (errors.length) {
          return alert(
            "CSV parse error: " + errors.map((e) => e.message).join(", ")
          );
        }

        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return alert("Please log in again.");

        try {
          const toInsert = rows.map((r, i) => {
            const date = r.date?.trim();
            const description = r.description?.trim();
            const amount = parseFloat(r.amount);
            const type = r.type?.trim();

            if (!date || isNaN(new Date(date)))
              throw new Error(`Row ${i + 1}: Invalid date`);
            if (!description)
              throw new Error(`Row ${i + 1}: Missing description`);
            if (isNaN(amount)) throw new Error(`Row ${i + 1}: Invalid amount`);
            if (!["Credit", "Debit"].includes(type))
              throw new Error(`Row ${i + 1}: type must be Credit or Debit`);

            // prevent importing legacy mirrors
            if (description.startsWith(MIRROR_PREFIX)) {
              throw new Error(
                `Row ${
                  i + 1
                }: Description must not start with "${MIRROR_PREFIX}"`
              );
            }

            return {
              date,
              description,
              amount,
              type,
              status: "Uncategorized",
              bank_account_id: selectedAccount,
              account_id: null,
              account_name: null,
              company_id: companyId,
              user_id: user.id,
              journal_id: null, // if the column exists; harmless if ignored by DB client? (better if you add column)
            };
          });

          const { error } = await supabase
            .from("bank_transactions")
            .insert(toInsert);
          if (error) throw error;

          alert(`Uploaded ${toInsert.length} transactions.`);
          setCsvFile(null);
          await fetchTabCounts();
          await fetchTransactions();
        } catch (err) {
          console.error(err);
          alert(err.message || "Upload failed.");
        }
      },
    });
  };

  /** Bulk actions */
  const handleCategorize = async () => {
    if (!selectedTransactions.length)
      return alert("Select transactions first.");
    if (!companyId) return alert("Company context not loaded.");

    // Ensure bank account selected
    if (!selectedAccount) return alert("Select a bank account first.");

    setIsCategorizing(true);

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setIsCategorizing(false);
      return alert("Please log in again.");
    }

    try {
      // Work from DB for safety (in case UI filtered list is stale)
      const { data: selectedRows, error: selErr } = await supabase
        .from("bank_transactions")
        .select("*")
        .in("id", selectedTransactions)
        .eq("company_id", companyId)
        .eq("bank_account_id", selectedAccount);

      if (selErr) throw selErr;

      const rows = selectedRows || [];

      // Validate account assignments
      for (const tx of rows) {
        if (tx.status === "Categorized") continue;

        const acctId = multiAccountId || accountSelections[tx.id] || null;
        if (!acctId) {
          throw new Error(
            `Please select an account for: ${tx.description} (${tx.date})`
          );
        }
      }

      // Process each tx: update bank row + create journal pair
      for (const tx of rows) {
        if (tx.status === "Categorized") continue;

        const categoryAccountId = multiAccountId || accountSelections[tx.id];
        const categoryAccountName =
          accounts.find((a) => a.id === categoryAccountId)?.name || null;

        const journalId = uuidv4();
        const narration = tx.description; // Option 1 (recommended): keep exact narration

        const [bankRow, categoryRow] = buildJournalRowsForBankTx({
          tx,
          companyId,
          bankAccountId: selectedAccount,
          categoryAccountId,
          description: narration,
        });

        // Insert journal entries (2 lines)
        const journalPayload = [
          { ...bankRow, journal_id: journalId },
          { ...categoryRow, journal_id: journalId },
        ];

        const { error: jErr } = await supabase
          .from("journal_entries")
          .insert(journalPayload);

        if (jErr) throw jErr;

        // Update bank tx to categorized + link journal_id
        const { error: updErr } = await supabase
          .from("bank_transactions")
          .update({
            status: "Categorized",
            account_id: categoryAccountId,
            account_name: categoryAccountName,
            user_id: user.id,
            journal_id: journalId, // requires column (recommended)
          })
          .eq("id", tx.id)
          .eq("company_id", companyId);

        if (updErr) throw updErr;
      }

      alert("Categorized successfully.");
      setActiveTab("Categorized");
      setSelectedTransactions([]);
      setAccountSelections({});
      setMultiAccountId("");
      await fetchTabCounts();
      await fetchTransactions();
    } catch (err) {
      console.error(err);
      alert("Error categorizing: " + (err.message || "Unknown error"));
    } finally {
      setIsCategorizing(false);
    }
  };

  const handleReverse = async () => {
    if (!selectedTransactions.length)
      return alert("Select transactions first.");
    if (!companyId) return alert("Company context not loaded.");
    if (!selectedAccount) return alert("Select a bank account first.");

    try {
      // Fetch selected tx rows from DB including journal_id
      const { data: selectedRows, error: selErr } = await supabase
        .from("bank_transactions")
        .select("id, status, journal_id")
        .in("id", selectedTransactions)
        .eq("company_id", companyId)
        .eq("bank_account_id", selectedAccount);

      if (selErr) throw selErr;

      const rows = selectedRows || [];

      // For each, delete linked journal entries (if journal_id exists)
      for (const tx of rows) {
        if (tx.status !== "Categorized") continue;

        if (tx.journal_id) {
          const { error: delJErr } = await supabase
            .from("journal_entries")
            .delete()
            .eq("company_id", companyId)
            .eq("journal_id", tx.journal_id);

          if (delJErr) throw delJErr;
        }
      }

      // Restore originals to Uncategorized
      const { error: updErr } = await supabase
        .from("bank_transactions")
        .update({
          status: "Uncategorized",
          account_id: null,
          account_name: null,
          journal_id: null,
        })
        .in("id", selectedTransactions)
        .eq("company_id", companyId);

      if (updErr) throw updErr;

      alert("Reversed successfully.");
      setActiveTab("Uncategorized");
      setSelectedTransactions([]);
      await fetchTabCounts();
      await fetchTransactions();
    } catch (err) {
      console.error(err);
      alert("Error reversing: " + (err.message || "Unknown error"));
    }
  };

  const handleDelete = async () => {
    if (!selectedTransactions.length)
      return alert("Select transactions first.");
    try {
      const { error } = await supabase
        .from("bank_transactions")
        .update({ status: "Deleted" })
        .in("id", selectedTransactions)
        .eq("company_id", companyId);

      if (error) throw error;

      alert("Moved to Deleted.");
      setActiveTab("Deleted");
      setSelectedTransactions([]);
      await fetchTabCounts();
      await fetchTransactions();
    } catch (err) {
      console.error(err);
      alert("Error deleting: " + (err.message || "Unknown error"));
    }
  };

  /** UI */
  return (
    <div className="tx-page">
      <div className="tx-header">
        <div className="tx-title">
          <h1>Bank Transactions</h1>
          <p className="tx-subtitle">
            Upload, filter and categorize transactions. Categorizing posts a
            balanced journal entry.
          </p>
        </div>

        <div className="tx-breadcrumb">
          <span className="tx-subtitle">Bank Account</span>
          <select
            value={selectedAccount}
            onChange={(e) => {
              setSelectedAccount(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Select</option>
            {bankAccounts.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="tx-panel">
        <div className="csv-upload-row">
          <a
            href="/upload_transaction.csv"
            download
            className="download-sample-btn"
          >
            Download Sample
          </a>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
          />
          <button
            className="btn"
            onClick={handleUploadCSV}
            disabled={!csvFile || !selectedAccount}
          >
            Upload CSV
          </button>
        </div>

        <h3 id="filter">Filters</h3>

        <div className="filter-bar">
          <input
            placeholder="Search description"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            <option>Credit</option>
            <option>Debit</option>
          </select>
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            title="From"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            title="To"
          />
          <input
            type="number"
            placeholder="Min amt"
            value={filterMinAmt}
            onChange={(e) => setFilterMinAmt(e.target.value)}
          />
          <input
            type="number"
            placeholder="Max amt"
            value={filterMaxAmt}
            onChange={(e) => setFilterMaxAmt(e.target.value)}
          />
        </div>

        <div className="transaction-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? "active" : ""}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
            >
              {tab} ({tabCounts[tab] || 0})
            </button>
          ))}
        </div>

        {selectedTransactions.length > 0 && (
          <div className="bulk-actions">
            {activeTab === "Uncategorized" ? (
              <>
                {selectedTransactions.length > 1 && (
                  <AccountSearchSelect
                    accounts={accounts}
                    value={multiAccountId}
                    onChange={setMultiAccountId}
                    placeholder="Search account for all selected transactions"
                  />
                )}
                <button
                  className="btn primary"
                  onClick={handleCategorize}
                  disabled={isCategorizing}
                >
                  Categorize
                </button>
                <button className="btn danger" onClick={handleDelete}>
                  Delete
                </button>
              </>
            ) : (
              <button className="btn" onClick={handleReverse}>
                Reverse
              </button>
            )}
          </div>
        )}

        {!transactions.length ? (
          <p className="bt-empty">No transactions.</p>
        ) : (
          <div className="bt-table-wrap">
            <table id="trans-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={toggleSelectAllOnPage}
                    />
                  </th>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount (₦)</th>
                  <th>Type</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(t.id)}
                        onChange={() => toggleSelectOne(t.id)}
                      />
                    </td>
                    <td>{t.date}</td>
                    <td>{t.description}</td>
                    <td>₦{Number(t.amount || 0).toLocaleString()}</td>
                    <td>{t.type}</td>
                    <td>
                      {activeTab === "Uncategorized" ? (
                        <AccountSearchSelect
                          accounts={accounts}
                          value={accountSelections[t.id] || ""}
                          onChange={(accountId) =>
                            setPerRowAccount(t.id, accountId)
                          }
                          placeholder="Search account"
                        />
                      ) : (
                        t.account_name || "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pageCount > 1 && (
          <div className="pagination">
            <button
              className="btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="page-indicator">
              Page {currentPage} of {pageCount}
            </span>
            <button
              className="btn"
              onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
              disabled={currentPage === pageCount}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
